<template>
    <div v-if="!finished">
        <div class="mx-auto w-1/2 text-center">
            <h1 class="first-letter:uppercase text-heading-1 font-semibold mr-auto mb-16">{{ $t('import.step_5.title') }}</h1>
            <p class="text-primary-80 mb-32">{{ $t('import.step_5.description') }}</p>
        </div>

        <div v-if="job_id && phase_id" class="grid grid-cols-1 gap-32 sm:grid-cols-3 mx-auto w-full">
            <div class="relative flex items-start rounded-sm border border-primary-5 bg-white p-32 shadow-sm">
                <div class="flex items-start space-x-24">
                    <svg-icon class="inline h-60 w-60" type="paper-05"/>

                    <div>
                        <strong v-if="applicationsCount" class="block text-secondary-80 text-heading-1 leading-none mb-12">{{ applicationsCount }}</strong>
                        {{ $t('import.step_5.new_applicants') }}
                    </div>
                </div>
            </div>
            <div class="relative flex items-start rounded-sm border border-primary-5 bg-white p-32 shadow-sm">
                <div class="flex items-start space-x-24">
                    <svg-icon class="inline h-60 w-60" type="paper-07"/>

                    <span class="block">
                        <strong v-if="jobName" class="block text-secondary-80 text-heading-1 leading-none mb-12">{{ jobName }}</strong>
                        {{ $t('import.step_5.jobName') }}
                    </span>
                </div>
            </div>
            <div class="relative flex items-start rounded-sm border border-primary-5 bg-white p-32 shadow-sm">
                <div class="flex items-start space-x-24">
                    <svg-icon class="inline h-60 w-60" type="paper-03"/>

                    <span class="block">
                        <strong v-if="stageName" class="block text-secondary-80 text-heading-1 leading-none mb-12">{{ stageName }}</strong>
                        {{ $t('import.step_5.stageName') }}
                    </span>
                </div>
            </div>
        </div>

        <div v-else class="grid grid-cols-1 gap-32 sm:grid-cols-2 mx-auto w-4/5">
            <div class="relative flex items-start rounded-sm border border-primary-5 bg-white p-32 shadow-sm">
                <div class="flex items-start space-x-24">
                    <svg-icon class="inline h-60 w-60" type="paper-10"/>

                    <span class="block">
                        <strong v-if="jobsCount" class="block text-secondary-80 text-heading-1 leading-none mb-12">{{ jobsCount }}</strong>
                        {{ $t('import.step_5.jobCount') }}
                    </span>
                </div>
            </div>
            <div class="relative flex items-start rounded-sm border border-primary-5 bg-white p-32 shadow-sm">
                <div class="flex items-start space-x-24">
                    <svg-icon class="inline h-60 w-60" type="paper-05"/>

                    <span class="block">
                        <strong v-if="applicationsCount" class="block text-secondary-80 text-heading-1 leading-none mb-12">{{ applicationsCount }}</strong>
                        {{ $t('import.step_5.applicationCount') }}
                    </span>
                </div>
            </div>
        </div>


        <div class="mt-48 flex justify-center space-x-20">
            <simple-button class="uppercase w-200"
                           look="secondary"
                           @click="$emit('previous')">
                {{ $t('general.back') }}
            </simple-button>

            <simple-button class="uppercase w-200"
                           display="secondary"
                           look="primary"
                           icon="save"
                           :loading="working"
                           @click="confirm">
                {{ $t('general.confirm') }}
            </simple-button>
        </div>
    </div>
    <div v-else>
        <div class="flex justify-center">
            <div class="mr-32">
                <h1 class="first-letter:uppercase text-heading-1 font-semibold mr-auto my-16"
                    v-html="$t('import.finished.title')"></h1>
                <p v-html="$t('import.finished.description')"></p>
                <br/><br/>
                <p class="text-body">{{ $t('import.finished.notification') }}</p>
            </div>

            <div class="w-288">
                <lottie-animation
                    v-if="finished"
                    :animationData="animation"
                    :autoPlay="true"
                    :loop="false"
                    :speed="1"
                    style="margin: 0 !important"
                />
            </div>
        </div>

        <div class="text-center mt-32">
            <router-link class="inline-flex items-center justify-center px-24 py-12 rounded font-semibold text-white bg-secondary-80 hover:bg-secondary group uppercase"
                         to="/jobs">
                {{ $t('import.finished.back')}}
            </router-link>
        </div>
    </div>
</template>

<script>
import uniq from 'lodash/uniq';
import SimpleButton from '../SimpleButton';
import {importData} from '../../../services/ImportService';
import Job from '../../../models/Job';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../../state/store';
import Stage from '../../../models/Stage';
import SvgIcon from '../SvgIcon';
import {LottieAnimation} from "lottie-web-vue";
import animation from '../../../../../../../public/interim/animations/coffee.json';

export default {
    components: {SvgIcon, SimpleButton, LottieAnimation},
    data() {
        return {
            finished: false,
            working: false,
            animation
        };
    },
    props: {
        file: File,
        data: Array,
        fields: Array,
        job_id: [Number, String],
        job_column: String,
        phase_id: [Number, String],
        phase_column: String,
        fieldOptions: Array,
        transformFields: Object,
        phases: Object,
    },
    computed: {
        ...mapState(useMainStore, ['locale']),
        jobsCount() {
            if (!this.phase_column) {
                return null;
            }

            return uniq(this.data.map(app => app[this.phase_column])).length;
        },
        phasesCount() {
            if (!this.job_column) {
                return null;
            }

            return uniq(this.data.map(app => app[this.job_column])).length;
        },
        applicationsCount() {
            return this.data.length;
        },
        jobName() {
            return useRepo(Job).find(this.job_id)?.title[this.locale];
        },
        stageName() {
            return useRepo(Stage).find(this.phase_id)?.name;
        },
    },
    methods: {
        confirm() {
            this.working = true;

            importData({
                data: this.data,
                transformFields: this.transformFields,
                job_id: this.job_id,
                job_column: this.job_column,
                phase_id: this.phase_id,
                phase_column: this.phase_column,
                phases: this.phases,
            }).then(() => {
                this.working = false;
                this.finished = true;
            });
        },
    },
};
</script>
